import React, { useMemo, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import * as Classes from '@blueprintjs/core/lib/cjs/common/classes'
import { Button } from '@blueprintjs/core/lib/cjs/components/button/buttons'
import { Dialog } from '@blueprintjs/core/lib/cjs/components/dialog/dialog'
import { FormGroup } from '@blueprintjs/core/lib/cjs/components/forms/formGroup'
import { InputGroup } from '@blueprintjs/core/lib/cjs/components/forms/inputGroup'
import { MenuItem } from '@blueprintjs/core/lib/cjs/components/menu/menuItem'
import { Spinner } from '@blueprintjs/core/lib/cjs/components/spinner/spinner'
import InputSelect from 'src/components/InputSelect'
import {
    searchCustomer,
    getCustomerResults,
} from 'src/store/modules/cmdb/customers'
import {
    ALL_OPTION,
    ALL_OPTION_ID,
    TEMPLATE_TYPE_OPTIONS,
    RUNBOOK_CATEGORY_OPTIONS,
    DEVICE_TYPE_OPTIONS,
    NO_RESULTS_MENU_ITEM_PROPS,
} from './utils'
import styles from './createTemplateModal.module.scss'

/**
 * Modal form for creating a runbook template.
 * @param {Object} props - Component props.
 * @param {Function} props.onCancel - Closes the modal.
 * @param {Function} [props.onSave] - Called with template payload on save.
 * @param {boolean} props.customersLoading - Indicates organization search loading state.
 * @param {Array} props.customers - Organization search results.
 * @param {{searchCustomer: Function}} props.actions - Bound redux actions.
 * @returns {JSX.Element}
 */
export const CreateTemplateModal = ({
    onCancel,
    onSave,
    customersLoading,
    customers,
    actions,
    initialValues,
    title,
    submitLabel,
}) => {
    const getNormalizedInitialValues = useCallback(() => {
        return {
            templateName: initialValues?.name || '',
            templateType: initialValues?.template_type || '',
            organization: String(initialValues?.organization || ALL_OPTION_ID),
            runbookCategory: initialValues?.runbook_category || ALL_OPTION_ID,
            deviceType: initialValues?.device_type || ALL_OPTION_ID,
        }
    }, [initialValues])

    const [templateName, setTemplateName] = useState(
        getNormalizedInitialValues().templateName
    )
    const [templateType, setTemplateType] = useState(
        getNormalizedInitialValues().templateType
    )
    const [organization, setOrganization] = useState(
        getNormalizedInitialValues().organization
    )
    const [runbookCategory, setRunbookCategory] = useState(
        getNormalizedInitialValues().runbookCategory
    )
    const [deviceType, setDeviceType] = useState(
        getNormalizedInitialValues().deviceType
    )

    useEffect(() => {
        const nextValues = getNormalizedInitialValues()
        setTemplateName(nextValues.templateName)
        setTemplateType(nextValues.templateType)
        setOrganization(nextValues.organization)
        setRunbookCategory(nextValues.runbookCategory)
        setDeviceType(nextValues.deviceType)
    }, [getNormalizedInitialValues])

    useEffect(() => {
        actions.searchCustomer('')
    }, [actions])

    const organizationOptions = useMemo(() => {
        const customerOptions = customers.map((customer) => ({
            id: String(customer.id),
            name: customer.name,
        }))
        const selectedOrganizationId = String(initialValues?.organization || '')
        const shouldIncludeSelectedOrganization =
            selectedOrganizationId &&
            selectedOrganizationId !== ALL_OPTION_ID &&
            !customerOptions.some(
                (option) => option.id === selectedOrganizationId
            )

        const selectedOrganizationOption = shouldIncludeSelectedOrganization
            ? [
                  {
                      id: selectedOrganizationId,
                      name:
                          initialValues?.organization_name ||
                          selectedOrganizationId,
                  },
              ]
            : []

        return [ALL_OPTION, ...selectedOrganizationOption, ...customerOptions]
    }, [customers, initialValues])

    const selectItemRenderer = useCallback(
        (item, { handleClick, modifiers }) => {
            return (
                <MenuItem
                    active={modifiers.active}
                    key={item.id}
                    onClick={handleClick}
                    text={item.name}
                />
            )
        },
        []
    )

    const organizationItemRenderer = useCallback(
        (item, { handleClick, modifiers }) => (
            <MenuItem
                active={modifiers.active}
                key={item.id}
                label={item.id === ALL_OPTION_ID ? undefined : item.id}
                onClick={handleClick}
                text={item.name}
            />
        ),
        []
    )

    const renderOrganizationInputValue = useCallback(
        (organizationItem) => (organizationItem ? organizationItem.name : ''),
        []
    )

    const renderSelectInputValue = useCallback((item) => item?.name || '', [])

    const getSelectedOption = useCallback((options, selectedId) => {
        return options.find((option) => option.id === selectedId) || null
    }, [])

    const isSaveDisabled = !templateName.trim() || !templateType

    const handleSave = () => {
        const isAllOrganizations = organization === ALL_OPTION_ID

        const payload = {
            name: templateName.trim(),
            template_type: templateType,
            organization: isAllOrganizations ? null : Number(organization),
            organization_scope: isAllOrganizations ? ALL_OPTION_ID : undefined,
            runbook_category: runbookCategory,
            device_type: deviceType,
        }

        if (onSave) {
            onSave(payload)
            return
        }

        onCancel()
    }

    return (
        <Dialog isOpen={true} onClose={onCancel} title={title}>
            <div className={Classes.DIALOG_BODY}>
                <FormGroup label="Template Name *" labelFor="template-name">
                    <InputGroup
                        id="template-name"
                        large
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="Enter template name"
                    />
                </FormGroup>

                <FormGroup label="Template Type *" labelFor="template-type">
                    <InputSelect
                        inputProps={{
                            id: 'template-type',
                            large: true,
                            placeholder: 'Select template type',
                        }}
                        selectedItem={getSelectedOption(
                            TEMPLATE_TYPE_OPTIONS,
                            templateType
                        )}
                        items={TEMPLATE_TYPE_OPTIONS}
                        itemRenderer={selectItemRenderer}
                        inputValueRenderer={renderSelectInputValue}
                        onItemSelect={(item) => setTemplateType(item.id)}
                        filterable={false}
                        noResults={<MenuItem {...NO_RESULTS_MENU_ITEM_PROPS} />}
                        popoverProps={{
                            minimal: true,
                            targetClassName: 'full-width',
                        }}
                        minimal={true}
                    />
                </FormGroup>

                <FormGroup label="Organization" labelFor="organization">
                    <InputSelect
                        inputProps={{
                            id: 'organization',
                            large: true,
                            rightElement: customersLoading ? <Spinner /> : null,
                        }}
                        selectedItem={getSelectedOption(
                            organizationOptions,
                            organization
                        )}
                        items={organizationOptions}
                        itemRenderer={organizationItemRenderer}
                        inputValueRenderer={renderOrganizationInputValue}
                        onQueryChange={(name) => actions.searchCustomer(name)}
                        onItemSelect={(item) => setOrganization(item.id)}
                        noResults={<MenuItem {...NO_RESULTS_MENU_ITEM_PROPS} />}
                        popoverProps={{
                            minimal: true,
                            targetClassName: 'full-width',
                        }}
                        minimal={true}
                    />
                </FormGroup>

                <FormGroup label="Runbook Category" labelFor="runbook-category">
                    <InputSelect
                        inputProps={{
                            id: 'runbook-category',
                            large: true,
                        }}
                        selectedItem={getSelectedOption(
                            RUNBOOK_CATEGORY_OPTIONS,
                            runbookCategory
                        )}
                        items={RUNBOOK_CATEGORY_OPTIONS}
                        itemRenderer={selectItemRenderer}
                        inputValueRenderer={renderSelectInputValue}
                        onItemSelect={(item) => setRunbookCategory(item.id)}
                        filterable={false}
                        noResults={<MenuItem {...NO_RESULTS_MENU_ITEM_PROPS} />}
                        popoverProps={{
                            minimal: true,
                            targetClassName: 'full-width',
                        }}
                        minimal={true}
                    />
                </FormGroup>

                <FormGroup label="Device Type" labelFor="device-type">
                    <InputSelect
                        inputProps={{
                            id: 'device-type',
                            large: true,
                        }}
                        selectedItem={getSelectedOption(
                            DEVICE_TYPE_OPTIONS,
                            deviceType
                        )}
                        items={DEVICE_TYPE_OPTIONS}
                        itemRenderer={selectItemRenderer}
                        inputValueRenderer={renderSelectInputValue}
                        onItemSelect={(item) => setDeviceType(item.id)}
                        filterable={false}
                        noResults={<MenuItem {...NO_RESULTS_MENU_ITEM_PROPS} />}
                        popoverProps={{
                            minimal: true,
                            targetClassName: 'full-width',
                        }}
                        minimal={true}
                    />
                </FormGroup>
            </div>
            <div className={Classes.DIALOG_FOOTER}>
                <div className={styles.footerActions}>
                    <Button large onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        intent="primary"
                        large
                        disabled={isSaveDisabled}
                        onClick={handleSave}
                    >
                        {submitLabel}
                    </Button>
                </div>
            </div>
        </Dialog>
    )
}

const mapStateToProps = (state) => {
    return {
        customersLoading: state.cmdb.customers.loading,
        customers: getCustomerResults(state),
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        actions: bindActionCreators(
            {
                searchCustomer,
            },
            dispatch
        ),
    }
}

CreateTemplateModal.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    customersLoading: PropTypes.bool.isRequired,
    customers: PropTypes.array.isRequired,
    initialValues: PropTypes.shape({
        name: PropTypes.string,
        template_type: PropTypes.string,
        organization: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        organization_name: PropTypes.string,
        runbook_category: PropTypes.string,
        device_type: PropTypes.string,
    }),
    title: PropTypes.string,
    submitLabel: PropTypes.string,
    actions: PropTypes.shape({
        searchCustomer: PropTypes.func.isRequired,
    }).isRequired,
}

CreateTemplateModal.defaultProps = {
    onSave: undefined,
    initialValues: undefined,
    title: 'Create runbook template',
    submitLabel: 'Create runbook template',
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateTemplateModal)
